import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { ReactComponent as AstronomicLogo } from '../../../../../asset/images/astronomic_logo_3.svg';
import MainButton from '../../../../../components/buttons/MainButton/MainButton';
import styles from './ConfirmEmailTemplate.module.scss';
import { useOutletContext } from 'react-router-dom';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import NavigationLink from '../../../../../components/links/NavigationLink/NavigationLink';
import InputMessage from '../../../../../components/texts/InputMessage/InputMessage';
import { ReactComponent as ArrowIcon } from '../../../../../asset/images/button_arrow.svg';
import classNames from 'classnames';
import useFormSubmitLoader from '../../../../../hooks/useFormSubmitLoader';
import CSSTransitionWrapper from '../../../../wrappers/CSSTransitionWrapper/CSSTransitionWrapper';
import AstronomicLoader from '../../../../../components/blocks/AstronomicLoader/AstronomicLoader';
import { get_env } from '../../../../../config/functions/app_functions';
import { GENERAL_CONTENT } from '../../../../../data/generalContent';
import ShowOnlyInSomeEnv from '../../../../../components/helpers/ShowOnlyInSomeEnv/ShowOnlyInSomeEnv';

interface SignUpConfirmEmailProps {
  onSubmitApiRequest(userId: string, verificationCode: string): Promise<{ success: boolean; message: string }>;
  onGetDemoCodeApiRequest(userId: string): Promise<{ verificationCode: string }>;
  onRequestVerificationCodeApiRequest(userId: string): Promise<{ success: boolean }>;
}

const SignUpConfirmEmail: FunctionComponent<SignUpConfirmEmailProps> = ({
  onSubmitApiRequest,
  onGetDemoCodeApiRequest,
  onRequestVerificationCodeApiRequest,
}) => {
  const isMobile = useMediaQuery('(max-width: 1279px)');
  const { isLoading, handleFormSubmitLoader } = useFormSubmitLoader();

  const { togglePopUp } =
    useOutletContext<{
      togglePopUp;
    }>();

  const [pastedCode, setPastedCode] = useState(false); // New state to track paste operation
  const [email, setEmail] = useState(localStorage.getItem('userEmail') || 'NO_EMAIL');
  const [demoCode, setDemoCode] = useState('');
  const [code, setCode] = useState('');
  const [inputValues, setInputValues] = useState(['', '', '', '', '', '']);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handlePaste = async (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault(); // Prevent the default paste behavior
    setErrorMessage('');

    // Retrieve clipboard data
    const clipboardText = event.clipboardData?.getData('text') || ''; // Fallback to empty string
    const sanitizedClipboardText = clipboardText.replace(/-/g, ''); // Remove hyphens

    // Ensure it matches the expected length
    if (sanitizedClipboardText.length === 6) {
      setCode(sanitizedClipboardText); // Set code first
      setInputValues(sanitizedClipboardText.split('')); // Set input values

      // Set pastedCode to true to indicate that the paste action is complete
      setPastedCode(true);

      // Focus the last input to complete the action visually
      const inputs = document.querySelectorAll('input[type="text"]');
      if (inputs[5]) {
        (inputs[5] as HTMLInputElement).focus();
      }
    }
  };

  useEffect(() => {
    if (pastedCode && code.length === 6) {
      handleOnSubmit(); // Trigger submit only after paste
      setPastedCode(false); // Reset pastedCode after submit
    }
  }, [code, pastedCode]); // Dependency on both `code` and `pastedCode`

  const handleInputChange = (index: number, value: string) => {
    setErrorMessage('');

    const newInputValues = [...inputValues];

    // Handle typing: Replace the current value with the typed value
    if (value.length === 1) {
      newInputValues[index] = value.toUpperCase();
      setInputValues(newInputValues);

      // Move to the next input if it's not the last one
      if (index < 5) {
        const nextInput = document.querySelector(`.input-${index + 1}`) as HTMLInputElement;
        if (nextInput) nextInput.focus();
      }
    }

    // Update the formatted code
    const formattedCode = newInputValues.join('');
    setCode(formattedCode);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    const newInputValues = [...inputValues];

    // Handle backspace/delete: If value is empty, move to the previous input
    if ((event.key === 'Backspace' || event.key === 'Delete') && inputValues[index] === '') {
      // If the current input is empty and delete/backspace is pressed, move to previous input
      if (index > 0) {
        const prevInput = document.querySelector(`.input-${index - 1}`) as HTMLInputElement;
        if (prevInput) {
          prevInput.focus();
        }
      }
    }

    // Handle backspace/delete when the input has a value
    if ((event.key === 'Backspace' || event.key === 'Delete') && inputValues[index] !== '') {
      newInputValues[index] = ''; // Clear the current input value
      setInputValues(newInputValues);

      // Move focus to the previous input if it is empty
      if (index > 0 && newInputValues[index - 1] === '') {
        const prevInput = document.querySelector(`.input-${index - 1}`) as HTMLInputElement;
        if (prevInput) {
          prevInput.focus();
        }
      }
    }

    // Update the formatted code
    const formattedCode = newInputValues.join('');
    setCode(formattedCode);
  };

  const handleOnSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrorMessage('');

    if (code === '') {
      setErrorMessage('Please, enter your code from email');
      return;
    }

    if (code.length < 6) {
      setErrorMessage('Please, fill in all characters');
      return;
    }

    // API LOGIC
    await handleFormSubmitLoader(async () => {
      const apiResponse = await onSubmitApiRequest(localStorage.getItem('userId') || '', code);

      if (apiResponse.success) {
        setSuccessMessage(apiResponse.message);
      } else {
        setSuccessMessage(''); // Clear success message in case of an error
        setErrorMessage(apiResponse.message);
      }
    });
    // API LOGIC END
  };

  const initialTime = 30; // Initial timer duration in seconds
  const [seconds, setSeconds] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);
  const timerIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const storedStartTime = localStorage.getItem('timerStartTime');

    if (storedStartTime) {
      const savedTime = new Date(storedStartTime).getTime();
      const currentTime = new Date().getTime();
      const elapsedTime = Math.floor((currentTime - savedTime) / 1000);
      const remainingTime = initialTime - elapsedTime;

      if (remainingTime > 0 && isRunning === false) {
        clearIntervalTimer();
        localStorage.setItem('timerStartTime', storedStartTime);
        setSeconds(remainingTime);
        startTimer(remainingTime); // Pass the remaining time to startTimer
      }
    }
  }, []);

  const startTimer = (remainingTime: number) => {
    setIsRunning(true);

    timerIntervalRef.current = window.setInterval(() => {
      if (remainingTime > 0) {
        setSeconds(remainingTime - 1); // Update seconds state
        remainingTime--;
      } else {
        clearIntervalTimer();
      }

      if (timerIntervalRef.current === null) {
        clearIntervalTimer();
      }
    }, 1000);
  };

  const clearIntervalTimer = () => {
    if (timerIntervalRef.current !== null) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
      setIsRunning(false);
      setSeconds(initialTime);
      localStorage.removeItem('timerStartTime');
    }
  };

  const handleStart = async () => {
    if (!isRunning) {
      requestVerificationCode();

      const startTime = new Date();
      localStorage.setItem('timerStartTime', startTime.toISOString());
      startTimer(seconds);
    }
  };

  const handleOnGetDemoCode = async () => {
    // API LOGIC
    const apiResponse = await onGetDemoCodeApiRequest(localStorage.getItem('userId') || '');

    if (apiResponse?.verificationCode) setDemoCode(apiResponse?.verificationCode);
    // API LOGIC END
  };

  const requestVerificationCode = async () => {
    // API LOGIC
    const apiResponse = await onRequestVerificationCodeApiRequest(localStorage.getItem('userId') || '');

    if (apiResponse.success === true) {
      handleOnGetDemoCode();
    }
    // API LOGIC END
  };

  return (
    <div className={styles['container']}>
      <div className={styles['container-top-part']}>
        <NavigationLink
          path="/"
          iconLeft={<ArrowIcon style={{ width: '11px', transform: 'rotate(180deg)' }} />}
          withBorder={false}
          colorType="white"
        >
          Back to Homepage
        </NavigationLink>
      </div>

      <div className={styles['content']}>
        <CSSTransitionWrapper onEnter={isLoading} styleVariation="onForeground">
          <AstronomicLoader variation="blurredBackground" color="onBlue" />
        </CSSTransitionWrapper>

        <div className={styles['top-row']}>
          <div className={styles['astronomic-logo']}>{<AstronomicLogo />}</div>

          {isMobile === true && (
            <MainButton sizeType="medium" visualType="white" onClick={() => togglePopUp(true)}>
              {GENERAL_CONTENT.HOW_ASTRONOMIC_WORKS}
            </MainButton>
          )}
        </div>
        <div className={styles['title']}>
          <h3 className="text text--h3">Check your email</h3>
        </div>
        <div className={styles['text']}>
          <p className="text text--body-2">
            We've sent a 6-digit code to {email}. The code expires shortly, so please enter it soon. <br />
            <span className={classNames(styles['text__1'], 'text text--body-3')}>
              If it hasn’t arrived in your inbox, please check your spam folder.
            </span>
          </p>
        </div>
        <form onSubmit={handleOnSubmit}>
          <div className={styles['code-container']}>
            <div className={styles['code-container__input-group']}>
              {inputValues.slice(0, 3).map((value, index) => {
                return (
                  <input
                    className={`${styles['code-container__input-group__input']} input-${index}`}
                    key={index}
                    type="text"
                    maxLength={1}
                    value={value}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    id={`unique-id-${index}`}
                  />
                );
              })}
            </div>

            <div className={styles['code-container__line']}></div>

            <div className={styles['code-container__input-group']}>
              {inputValues.slice(3).map((value, index) => {
                const newIndex = index + 3;

                return (
                  <input
                    className={`${styles['code-container__input-group__input']} input-${newIndex}`}
                    key={newIndex}
                    type="text"
                    maxLength={1}
                    value={value}
                    onChange={(e) => handleInputChange(newIndex, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(newIndex, e)}
                    onPaste={handlePaste}
                    id={`unique-id-${newIndex}`}
                  />
                );
              })}
            </div>
          </div>

          {errorMessage && <InputMessage>{errorMessage}</InputMessage>}
          {successMessage && <InputMessage messageState="success">{successMessage}</InputMessage>}

          <div className={styles['button']}>
            <MainButton sizeType="large" disabled={isLoading}>
              Continue
            </MainButton>
          </div>
        </form>

        <div className={styles['resend']}>
          <p className="text text--body-2">Haven't received an email?</p>

          {isRunning === false ? (
            <NavigationLink
              path="#"
              sizeType="small"
              className={styles['resend__link']}
              onClick={handleStart}
              colorType="blue"
            >
              Resend
            </NavigationLink>
          ) : (
            <NavigationLink
              path="#"
              sizeType="small"
              colorType="blue"
              className={classNames(styles['resend__link'], styles['resend__link--disabled'])}
            >
              Resend
            </NavigationLink>
          )}
        </div>

        {isRunning === true && (
          <div className={styles['resend-timer']}>
            <p className="text text--body-2">
              Before next resend: <span className="text text--bold">{seconds}</span> sec
            </p>
          </div>
        )}

        <ShowOnlyInSomeEnv showInWhatEnv={['development', 'staging']}>
          <div style={{ marginTop: '24px', opacity: '0.1' }}>
            <MainButton sizeType="small" visualType="white" onClick={handleOnGetDemoCode}>
              Get demo code
            </MainButton>

            {demoCode && (
              <div style={{ color: 'var(--color-white)' }}>
                <p style={{ marginTop: '24px' }} className={classNames('text text--body-2')}>
                  Demo code:
                </p>

                <p className={classNames('text text--body-2 text--bold')}>{demoCode}</p>
              </div>
            )}
          </div>
        </ShowOnlyInSomeEnv>
      </div>

      <div className={styles['container-bottom-part']}></div>
    </div>
  );
};

export default SignUpConfirmEmail;
